import React from 'react'
import { Checkbox, Col, Flex, Row, Spin, Switch, Typography } from 'antd'
import { Checklist, ChecklistGroupItem, ChecklistItem, ChecklistItemAssignee } from '@/common/types/checklist.def.ts'
import dayjs from 'dayjs'
import { cn } from '@/utils/style.ts'
import { handleApiError } from '@/utils/api.tsx'
import { useUpdateChecklistItemMutation } from '@/features/checklist/checklist.api.ts'
import { EmployeeTooltip } from '@/common/components/EmployeeTooltip.tsx'
import { useChecklistDetails } from '@/pages/checklist/components/ChecklistDetails.tsx'

type ChecklistGroupItemProps = {
  checklist: Checklist
  group: ChecklistGroupItem
}

export const ChecklistGroupItemSection: React.FC<ChecklistGroupItemProps> = ({
  checklist,
  group,
}) => {
  const { filteredAssignees } = useChecklistDetails()
  const filteredDict = filteredAssignees.reduce((acc, curr) => {
    acc[curr] = true
    return acc
  }, {} as Record<string, boolean>)

  const displayItems = group.items
      .filter(x => filteredDict[x.assignee])
      .sort((a, b) => a.deadline < b.deadline ? -1 : 1)

  return (
    <Flex vertical key={group.groupName} className="max-w-[1600px]">
      <Row gutter={16}>
        <Col span={10}>
          <Typography.Title level={4}>{group.groupName}</Typography.Title>
        </Col>
        <Col span={2}>
          <Typography.Text className="font-bold">Assignee</Typography.Text>
        </Col>
        <Col span={2}>
          <Typography.Text className="font-bold">Deadline</Typography.Text>
        </Col>
        <Col span={2}>
          <Typography.Text className="font-bold">Applicable</Typography.Text>
        </Col>
        <Col span={6} />
      </Row>
      {displayItems.map((item) => (
        <ChecklistRow
          key={item.order}
          groupName={group.groupName}
          checklist={checklist}
          item={item}
          checklistId={checklist.id}
        />
      ))}
    </Flex>
  )
}

type ChecklistRowProps = {
  checklistId: number
  checklist: Checklist
  groupName: string
  item: ChecklistItem
}

const ChecklistRow: React.FC<ChecklistRowProps> = ({
  checklistId,
  checklist,
  groupName,
  item,
}) => {
  const [updateItem, { isLoading }] = useUpdateChecklistItemMutation()

  const latestActionLog = item.actionLogs[item.actionLogs.length - 1]
  const isTargetLog = latestActionLog?.action === 'checked' || latestActionLog?.action === 'disabled'

  const handleCheckboxChange = async () => {
    const result = await updateItem({
      groupName,
      order: item.order,
      checklistId: checklistId,
      newCheckValue: !item.complete,
      disabled: null,
    })
    if ('error' in result) {
      handleApiError(result.error)
    }
  }

  const handleDisable = async () => {
    const result = await updateItem({
      groupName,
      order: item.order,
      checklistId: checklistId,
      newCheckValue: null,
      disabled: !item.disabled,
    })
    if ('error' in result) {
      handleApiError(result.error)
    }
  }

  const deadlineColor = (date: string) => {
    const deadline = dayjs(date, 'YYYY-MM-DD')
    const today = dayjs()
    const diff = deadline.diff(today, 'day')
    if (diff < 0) return 'text-red-400'
    if (diff < 7) return 'text-orange-400'
    return ''
  }

  return (
    <Row gutter={16} className="hover:bg-gray-50 p-1">
      <Col span={10}>
        <Flex gap="small" align="flex-start" justify="flex-start">
          {isLoading
            ? <Spin size={'small'} />
            : <Checkbox
              checked={item.complete && !item.disabled}
              onChange={handleCheckboxChange}
              disabled={!item.canWrite || item.disabled}
            />
          }
          <Typography.Text
            className={cn('whitespace-break-spaces', {
              'text-gray-500': item.complete,
              'line-through': item.disabled,
            })}
          >
            {item.title}
          </Typography.Text>
        </Flex>
      </Col>
      <Col span={2}>
        {item.assignee === ChecklistItemAssignee.LineManager && !!checklist.lineManagerEmployeeId ?
          <EmployeeTooltip employeeId={checklist.lineManagerEmployeeId} /> : item.assignee}
      </Col>
      <Col span={2}>
        <Typography
          className={cn({
            'text-gray-500': item.complete,
            [deadlineColor(item.deadline)]: !item.complete && !item.disabled,
            'line-through': item.disabled,
          })}
        >
          {item.deadline}
        </Typography>
      </Col>
      <Col span={2}>
        <Switch
          size="small"
          loading={isLoading}
          value={!item.disabled}
          onChange={handleDisable}
          disabled={!item.canWrite}
        />
      </Col>
      <Col span={6}>
        {latestActionLog && isTargetLog &&
          <Typography
            className={cn('text-emerald-400', {
              'text-amber-400': latestActionLog.action === 'disabled',
            })}
          >
            {latestActionLog.action} by {latestActionLog.username}, {dayjs(latestActionLog.timestamp).format(
            'YYYY-MM-DD HH:mm:ss Z')}
          </Typography>}
      </Col>
    </Row>
  )
}